import React, { useState } from 'react';
import { LeftChevronSVG, RightChevronSVG } from 'components/svg';
import useWindowSize from 'shared/hooks/useWindowSize';
import classnames from 'classnames';
import { createGuid } from 'shared/components/hawksearch/helpers/Cookies';

interface PagerProps {
	page: number;
	totalPages: number;

	onPageChange: (page: number) => void;
}

function Pager({ page, totalPages, onPageChange }: PagerProps) {
	const [inputValue, setInputValue] = useState<string | undefined>(undefined);
	const [hasError, setHasError] = useState(false);
	const [uniqueId] = useState(`page-input-${createGuid()}`);

	const size = useWindowSize();

	function goToPreviousPage(e) {
		e.preventDefault();

		goToPage(page - 1);
	}

	function goToNextPage(e) {
		e.preventDefault();

		goToPage(page + 1);
	}

	function goToPage(pageNo: number) {
		if (isNaN(pageNo)) {
			// not a valid number
			doInputError();
			return;
		}

		if (pageNo < 1) {
			// can't go beyond the first page
			doInputError();
			return;
		}

		if (pageNo > totalPages) {
			// can't go beyond the last page
			doInputError();
			return;
		}

		// once we've determined that we *do* want to do this page change, clear the user's input
		// because the input should be driven by props again
		setInputValue(undefined);

		// inform the consumer that we've changed pages
		onPageChange(pageNo);
	}

	/**
	 * Returns the input value for the pager input control. If the user has typed in a value into the input then
	 * that value will be returned; otherwise, the page value passed in via props will be returned.
	 */
	function getInputValue() {
		if (inputValue !== undefined) {
			// if the user typed an input, that's the page value for the control
			return inputValue || '';
		}

		// otherwise, fall back to what's passed in through props
		return page;
	}

	function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === 'Enter') {
			const wantedPageNo = parseInt(event.currentTarget.value, 10);
			goToPage(wantedPageNo);
		}
	}

	function doInputError() {
		setHasError(true);

		// in 500ms, clear the error animation
		setTimeout(() => {
			setHasError(false);
		}, 500);
	}

	function onChange(event: React.ChangeEvent<HTMLInputElement>) {
		setInputValue(event.currentTarget.value);
	}

	const prevSearchParams = new URLSearchParams(window.location.search);
	prevSearchParams.set('pg', (page > 1 ? page - 1 : page).toString());

	const nextSearchParams = new URLSearchParams(window.location.search);
	nextSearchParams.set('pg', (page === totalPages ? page : page + 1).toString());

	return (
		<div className="hawk-pagination__controls">
			<a
				className={classnames('hawk-pagination__item hawk-pagination__item--left', {
					'hawk-btn': size && size.width && size.width < 768,
				})}
				onClick={goToPreviousPage}
				href={window.location.pathname + '?' + prevSearchParams.toString()}
			>
				<LeftChevronSVG class="hawk-pagination__left" />
				<span className="roc-visually-hidden">Previous page</span>
			</a>

			<label htmlFor={uniqueId}>
				<input
					type="number"
					min="1"
					max={totalPages}
					value={getInputValue()}
					onChange={onChange}
					onKeyDown={onKeyDown}
					className={hasError ? 'hawk-pagination__input error' : 'hawk-pagination__input'}
					id={uniqueId}
				/>
			</label>
			<span className="hawk-pagination__total-text">&nbsp; of {totalPages}</span>
			<a
				className={classnames('hawk-pagination__item hawk-pagination__item--right', {
					'hawk-btn': size && size.width && size.width < 768,
				})}
				onClick={goToNextPage}
				href={window.location.pathname + '?' + nextSearchParams.toString()}
			>
				<RightChevronSVG class="hawk-pagination__right" />
				<span className="roc-visually-hidden">Next page</span>
			</a>
		</div>
	);
}

export default Pager;
